import { ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges, inject, DestroyRef } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError } from 'rxjs/operators';
import { CreditsPartIIIF8936SchAService } from './credits-part-iii-f8936-sch-a.service';
import { GtwSnackbarService } from '../../../../shared/_services/gtw-snackbar.service';

import { safeJsonInput } from '../../../../shared/_utils/safe-json-input';
import { safeEvalFormula, isFiniteEvalNumber } from '../../../../shared/_utils/safe-eval-formula';

@Component({
    selector: 'gtw-credits-part-iii-f8936-sch-a',
    templateUrl: './credits-part-iii-f8936-sch-a.component.html',
    styleUrls: ['./credits-part-iii-f8936-sch-a.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CreditsPartIIIF8936SchAComponent implements OnInit, OnChanges {
  private readonly destroyRef = inject(DestroyRef);

  @Input() activeTab: string = '';
  @Input() rowData: any = {};
  @Input() filterParams: any = {};
  @Input() globalService: any = {};
  @Input() generalConfig: any = {};
  @Input() userSettings: any = {};
  @Input() formDataObj: any = {};

  @Output() cancel = new EventEmitter<void>();

  loadingDataSchdA = false;
  loadingDtlsSchdA = true;
  isSaveClickedSchdA = false;
  isEdit = false;
  isNewClicked = false;

  form8936SchdADtls: any[] = [];
  formHeaderSchdA: any[] = [];
  tableHeaderSchdA: any[] = [];
  formListSchdA: any[] = [];
  outerListSchdA: any[] = [];
  formCalcSchdAData: any[] = [];

  private allTabsListSchdA: any[][] = [];

  constructor(
    private service: CreditsPartIIIF8936SchAService,
    private snackbar: GtwSnackbarService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.fetchAllDetails();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['rowData'] && !changes['rowData'].firstChange) {
      this.fetchAllDetails();
    }
  }

  private get baseUrl(): string {
    return this.generalConfig?.base_url || '';
  }

  private get currentYear(): any {
    return this.filterParams?.tax_year || this.rowData?.TAX_YEAR;
  }

  private get scenario(): any {
    return this.filterParams?.scenario || this.rowData?.SCENARIO;
  }

  private get jcdKey(): any {
    return this.filterParams?.jcd_key || this.rowData?.JCD_KEY || this.globalService?.jcd_key;
  }

  private get combinationKey(): any {
    return this.rowData?.COMBINATION_KEY;
  }

  private get issueKey(): any {
    return this.rowData?.ISSUE_KEY;
  }

  private get ssoId(): any {
    return this.userSettings?.user?.sso_id || '';
  }

  fetchAllDetails(): void {
    this.loadingDtlsSchdA = true;
    this.cdr.detectChanges();
    this.service.loadJsonObject(this.baseUrl, {
      action_code: 'j35iiy',
      tax_year: this.currentYear,
      scenario: String(this.scenario || ''),
      jcd_key: this.jcdKey,
      combination_key: this.combinationKey ? String(this.combinationKey) : '',
      issue_key: '1',
      form_key: '1080'
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res: any) => {
      const rows: any[] = Array.isArray(res?.jsonObject) ? res.jsonObject : [];
      this.form8936SchdADtls = rows.map((r: any) => ({
        ...r,
        OCCURRENCE_DATA: this.safeParse(r.OCCURRENCE_DATA)
      }));
      this.loadingDtlsSchdA = false;
      this.cdr.detectChanges();
    }, () => {
      this.loadingDtlsSchdA = false;
      this.cdr.detectChanges();
    });
  }

  /**
   * Returns the formula list as Observable so callers can join with the
   * template fetch via forkJoin and run init-calc only after BOTH are ready.
   */
  private fetchSchACalc$() {
    return this.service.loadJsonObject(this.baseUrl, {
      action_code: '1ubsuu',
      tax_year: this.currentYear,
      scenario: this.scenario,
      jcd_key: this.jcdKey,
      form_no: 'F8936SchA',
      schedule_name: 'A',
      part_no: '',
      section_name: ''
    }).pipe(catchError(() => of({ jsonObject: [] })));
  }

  addNewRow(): void {
    this.isNewClicked = true;
    this.isEdit = false;
    this.loadingDataSchdA = true;
    this.cdr.detectChanges();
    const template$ = this.service.loadJsonObject(this.baseUrl, {
      action_code: 'tryvqq',
      sso_id: this.ssoId,
      tax_year: this.currentYear,
      scenario: this.scenario,
      jcd_key: this.jcdKey,
      combination_key: this.combinationKey,
      form_no: 'F8936SchA',
      schedule_name: 'A',
      part_no: '',
      section_name: '',
      issue_key: this.issueKey
    });
    forkJoin([this.fetchSchACalc$(), template$]).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      ([calcRes, tmplRes]: [any, any]) => {
        this.formCalcSchdAData = Array.isArray(calcRes?.jsonObject) ? calcRes.jsonObject : [];
        this.applyTemplate(tmplRes?.jsonObject);
      },
      () => {
        this.loadingDataSchdA = false;
        this.cdr.detectChanges();
      }
    );
  }

  editRow(item: any): void {
    this.isEdit = true;
    this.isNewClicked = false;
    this.loadingDataSchdA = true;
    this.cdr.detectChanges();
    const template$ = this.service.loadJsonObject(this.baseUrl, {
      action_code: '9znv2f',
      sso_id: this.ssoId,
      tax_year: this.currentYear,
      scenario: this.scenario,
      jcd_key: this.jcdKey,
      combination_key: this.combinationKey,
      form_no: 'F8936SchA',
      schedule_name: 'A',
      part_no: '',
      section_name: '',
      issue_key: this.issueKey,
      occurrence: item?.OCCURRENCE
    });
    forkJoin([this.fetchSchACalc$(), template$]).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      ([calcRes, tmplRes]: [any, any]) => {
        this.formCalcSchdAData = Array.isArray(calcRes?.jsonObject) ? calcRes.jsonObject : [];
        this.applyTemplate(tmplRes?.jsonObject, item);
      },
      () => {
        this.loadingDataSchdA = false;
        this.cdr.detectChanges();
      }
    );
  }

  deleteRow(item: any): void {
    if (!item?.STAGE_KEY) return;
    this.service.deleteStageRecord(this.baseUrl, item.STAGE_KEY, this.ssoId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data?.callSuccess === '1' || data?.callSuccess === 1) {
          this.snackbar.openSnackBar('Vehicle Deleted Successfully.', 'gtw-snackbar--green');
          this.fetchAllDetails();
        } else {
          this.snackbar.openSnackBar(data?.errorMessage || 'Delete failed.', 'gtw-snackbar--red');
        }
      },
      () => {
        this.snackbar.openSnackBar('Delete failed.', 'gtw-snackbar--red');
      }
    );
  }

  private applyTemplate(jsonObject: any[] | null | undefined, item?: any): void {
    const list = Array.isArray(jsonObject) ? jsonObject : [];
    this.formHeaderSchdA = list.filter((x: any) => x.ROW_TYPE === 'FH');
    this.tableHeaderSchdA = list.filter((x: any) => x.ROW_TYPE === 'TH');

    let formListSchdA = list.slice(1).filter((x: any) => x.ROW_TYPE !== 'TH');
    if (this.isEdit && item && this.form8936SchdADtls.length > 0) {
      formListSchdA = formListSchdA.filter((o: any) => o.OCCURRENCE == item.OCCURRENCE);
    }
    this.formListSchdA = formListSchdA;

    this.assignSchdAParts(this.formListSchdA);
    for (const row of this.formListSchdA) this.processDateField(row);

    const outerListSchdA: any[] = [];
    for (const row of this.formListSchdA) {
      if (row?.FORM_LINE_KEY != null) {
        const exists = outerListSchdA.find(
          (x: any) => x.FORM_LINE_KEY === row.FORM_LINE_KEY && x.IS_FORM_DISPLAY === 'Y'
        );
        if (!exists) outerListSchdA.push(row);
      }
    }
    this.outerListSchdA = outerListSchdA;
    this.allTabsListSchdA = [this.formListSchdA];
    // Run a full pass of formula evaluation so calculated cells (lines 11, 12,
    // 15, 17, 21, 24, 26 etc.) are filled in before the user touches anything.
    this.validateSubFormSchdA();
    this.loadingDataSchdA = false;
    this.cdr.detectChanges();
  }

  /**
   * Populate formName/schdName/partName/sectionName on each row so save()
   * can emit them in the payload (mirrors legacy AngularJS behavior where
   * vm.formDataObj["formName"] etc. is split from SUB_FORM).
   */
  private assignSchdAParts(rows: any[]): void {
    if (!Array.isArray(rows) || rows.length === 0) return;
    const partName = (this.formDataObj?.partName) || this.derivePartName();
    for (const row of rows) {
      if (!row) continue;
      if (row.formName === undefined) row.formName = 'F8936SchA';
      if (row.schdName === undefined) row.schdName = 'A';
      if (row.partName === undefined) row.partName = partName;
      if (row.sectionName === undefined) row.sectionName = this.formDataObj?.sectionName || '';
    }
  }

  private derivePartName(): string {
    const sub = (this.rowData?.SUB_FORM || '').toString();
    if (sub.indexOf('F8936PV') >= 0) return 'V';
    if (sub.indexOf('F8936PII') >= 0) return 'II';
    return '';
  }

  /**
   * Replicates legacy AngularJS behavior: parse json formula on dateVarious cells
   * to populate item.taxYearBeginDate / item.taxYearEndDate (used as min/max in
   * the datepicker), and convert ATTRIB_VALUE to a Date object when not 'VARIOUS'.
   */
  private processDateField(item: any): void {
    if (!item || item.EDIT_TYPE !== 'custom_date_various') return;
    if (item.ATTRIB_VALUE !== 'VARIOUS' && item.ATTRIB_VALUE) {
      const parsed = this.safeParseDate(item.ATTRIB_VALUE);
      if (parsed) item.ATTRIB_VALUE = parsed;
    }
    const formula = item.ATTRIB_CALC_FORMULA;
    if (formula && typeof formula === 'string' && formula.substring(0, 4) === 'json') {
      try {
        const subFormula = safeJsonInput<any>(formula.slice(4), null, 'credits-part-iii-f8936-sch-a.parse1');
        if (subFormula[0]?.DEFAULT_FROM_DATE === 'CURR_RP_BEGIN') {
          item.taxYearBeginDate = this.safeParseDate(item.TAX_YEAR_BEGIN || this.formDataObj?.taxYearBegin);
        } else if (subFormula[0]?.DEFAULT_FROM_DATE === 'CURR_TY_BEGIN') {
          item.taxYearBeginDate = this.safeParseDate('01/01/' + this.currentYear);
        } else if (subFormula[0]?.DEFAULT_FROM_DATE) {
          item.taxYearBeginDate = this.safeParseDate(subFormula[0].DEFAULT_FROM_DATE);
        }
        if (subFormula[1]?.DEFAULT_TO_DATE === 'CURR_RP_END') {
          item.taxYearEndDate = this.safeParseDate(item.TAX_YEAR_END || this.formDataObj?.taxYearEnd);
        } else if (subFormula[1]?.DEFAULT_TO_DATE === 'CURR_TY_END') {
          item.taxYearEndDate = this.safeParseDate('12/31/' + this.currentYear);
        } else if (subFormula[1]?.DEFAULT_TO_DATE) {
          item.taxYearEndDate = this.safeParseDate(subFormula[1].DEFAULT_TO_DATE);
        }
      } catch (e) { /* bad json formula — ignore */ }
    }
    if (!item.taxYearBeginDate && this.formDataObj?.taxYearBeginDate) {
      item.taxYearBeginDate = this.formDataObj.taxYearBeginDate;
    }
    if (!item.taxYearEndDate && this.formDataObj?.taxYearEndDate) {
      item.taxYearEndDate = this.formDataObj.taxYearEndDate;
    }
  }

  private safeParseDate(value: any): Date | undefined {
    if (!value) return undefined;
    try {
      const d = (value instanceof Date) ? value : new Date(value);
      return isNaN(d.getTime()) ? undefined : d;
    } catch (e) {
      return undefined;
    }
  }

  getCellsForRow(lineKey: any): any[] {
    return (this.formListSchdA || []).filter(
      (x: any) => x.FORM_LINE_KEY === lineKey
        && x.IS_FORM_DISPLAY === 'Y'
        && x.EDIT_TYPE !== 'radio'
    );
  }

  getRadioCellsForRow(lineKey: any): any[] {
    return (this.formListSchdA || []).filter(
      (x: any) => x.FORM_LINE_KEY === lineKey
        && x.EDIT_TYPE === 'radio'
    );
  }

  get displayedOuter(): any[] {
    return (this.outerListSchdA || []).filter((r: any) => r.IS_FORM_DISPLAY === 'Y');
  }

  isRegularRow(row: any): boolean {
    return row?.ROW_TYPE !== 'PH' && row?.ROW_TYPE !== 'NT';
  }

  onFieldChange(item: any, event: any): void {
    const value = event?.target?.value ?? event;
    item.ATTRIB_VALUE = value;
    item.IS_CHANGED_FLAG = 'Y';
    this.validateSubFormSchdA(item);
  }

  onCheckboxChange(item: any, event: any): void {
    item.ATTRIB_VALUE = event?.target?.checked ? 'true' : 'false';
    item.IS_CHANGED_FLAG = 'Y';
    this.validateSubFormSchdA(item);
  }

  onDateChange(item: any, event: any): void {
    const val = event?.target?.value;
    if (!val) {
      item.ATTRIB_VALUE = '';
    } else {
      const [y, m, d] = String(val).split('-');
      item.ATTRIB_VALUE = `${m}/${d}/${y}`;
    }
    item.IS_CHANGED_FLAG = 'Y';
    this.validateSubFormSchdA(item);
  }

  /**
   * Handler invoked by <gtw-credits-date-various>. The widget already mutated
   * row.ATTRIB_VALUE and set IS_CHANGED_FLAG='Y'. Trigger change detection and
   * recalc dependent cells.
   */
  onDateVariousChange(item: any): void {
    this.validateSubFormSchdA(item);
    this.cdr.detectChanges();
  }

  onRadioChange(row1: any): void {
    const siblings = (this.formListSchdA || []).filter(
      (x: any) => x.FORM_LINE_KEY === row1.FORM_LINE_KEY
        && x.ATTRIB_COL === row1.ATTRIB_COL
        && x.IS_FORM_DISPLAY === 'Y'
    );
    for (const sibling of siblings) {
      sibling.ATTRIB_VALUE = (sibling.LINE_ATTRIB_KEY === row1.LINE_ATTRIB_KEY)
        ? (sibling.ATTRIB_VALUE === 'true' ? 'false' : 'true')
        : 'false';
      sibling.IS_CHANGED_FLAG = 'Y';
    }
    this.validateSubFormSchdA();
  }

  /**
   * Mirror of legacy AngularJS `vm.validateSubFormSchdA` (FormCreditsPartIIICtrl.js)
   * and its Angular twin in `app/F1120/.../form-credits-part-iii.component.ts:1254`.
   * Iterates every formula returned by action_code `1ubsuu` for `F8936SchA`
   * and runs `calcAmtSchdA`. Called both on init (after applyTemplate) and on
   * every input change so calculated cells stay in sync with edited inputs.
   */
  validateSubFormSchdA(line?: any): void {
    if (line != null && (line.DATA_TYPE === 'number' || line.DATA_TYPE === 'decimal')) {
      line.IS_CHANGED_FLAG = 'Y';
    }
    if (line == null || line.DATA_TYPE === 'number' || line.DATA_TYPE === 'decimal') {
      const formulas = this.formCalcSchdAData || [];
      for (let g = 0; g < formulas.length; g++) {
        this.calcAmtSchdA(
          formulas[g].ATTRIB_CALC_FORMULA,
          formulas[g].LINE_NO,
          formulas[g].LINE_ATTRIB_KEY,
          formulas[g].ATTRIB_COL,
          formulas[g].EDIT_TYPE
        );
      }
    } else if (line) {
      line.IS_CHANGED_FLAG = 'Y';
    }
    this.cdr.detectChanges();
  }

  /**
   * Universal evaluator for ATTRIB_CALC_FORMULA — handles two flavors:
   *  - JSON conditional formulas: `json[{SOURCE_NAME, SOURCE_VALUE, CONDITION,
   *    CALC_AMT|CALC_FORMULA_AMT, ALERT?, ALERT_VALUE?}, ...]`
   *  - Arithmetic formulas: `(F8936_A_I_X) op (F8936_A_I_Y)`, `Math.min/max(...)`,
   *    constant multipliers like `(F8936_A_I_14) * (0.30)`.
   * Substitutes each `(TOKEN)` with `parseFloat(ATTRIB_VALUE)` from
   * `formListSchdA` and `eval`s the resulting expression. Mirrors the existing
   * working implementation in F1120 form-credits-part-iii component.
   */
  calcAmtSchdA(formula: string, line_no: string, attrib_key: any, attrib_col: string, _edit_type: string): void {
    if (!formula) return;
    // Match the cell to write to by LINE_NO + ATTRIB_COL. We can't use
    // LINE_ATTRIB_KEY because the formulas come from a template-level fetch
    // (action_code `1ubsuu`) whose LINE_ATTRIB_KEYs are NOT the same as the
    // per-occurrence LINE_ATTRIB_KEYs returned by the data fetch (action_code
    // `tryvqq` / `9znv2f`). Schedule A is keyed per vehicle, so each row gets
    // its own LINE_ATTRIB_KEY at insert time. (LINE_NO, ATTRIB_COL) is what
    // uniquely identifies the destination cell in the rendered template.

    if (formula.substring(0, 4) === 'json') {
      try {
        const subFormula = safeJsonInput<any>(formula.slice(4), null, 'credits-part-iii-f8936-sch-a.parse2');
        for (let d = 0; d < subFormula.length; d++) {
          let isConditionResult = false;
          const sourceNameVal = this.formListSchdA.filter((o: any) => o.ATTRIB_SHORT_CODE === subFormula[d].SOURCE_NAME);
          if (!sourceNameVal || sourceNameVal.length === 0) continue;
          const sv = sourceNameVal[0].ATTRIB_VALUE;
          if (subFormula[d].CONDITION) {
            if (subFormula[d].CONDITION === '<=' && sv !== undefined && parseInt(sv, 10) <= parseInt(subFormula[d].SOURCE_VALUE, 10)) {
              isConditionResult = true;
            } else if (subFormula[d].CONDITION === '>' && sv !== undefined && parseInt(sv, 10) > parseInt(subFormula[d].SOURCE_VALUE, 10)) {
              isConditionResult = true;
            } else if (subFormula[d].CONDITION === '!=' && sv !== undefined && String(sv).trim() !== subFormula[d].SOURCE_VALUE) {
              isConditionResult = true;
            }
          } else if (sv !== undefined && sv == subFormula[d].SOURCE_VALUE) {
            isConditionResult = true;
          }

          if (isConditionResult) {
            const calcAttribVal = this.formListSchdA.filter((x: any) => x.LINE_NO === line_no && x.ATTRIB_COL === attrib_col);
            if (calcAttribVal.length > 0) {
              if (subFormula[d].CALC_FORMULA_AMT) {
                const target = this.formListSchdA.filter((x: any) => x.ATTRIB_SHORT_CODE === subFormula[d].CALC_FORMULA_AMT);
                if (target.length > 0) calcAttribVal[0].ATTRIB_VALUE = target[0].ATTRIB_CALC_FORMULA;
              } else {
                const target = this.formListSchdA.filter((x: any) => x.ATTRIB_SHORT_CODE === subFormula[d].CALC_AMT);
                if (target.length > 0) calcAttribVal[0].ATTRIB_VALUE = target[0].ATTRIB_VALUE;
              }
              calcAttribVal[0].IS_CHANGED_FLAG = 'Y';
              if (subFormula[d].ALERT === 'Y') {
                const targetName = this.formListSchdA.filter((x: any) => x.ATTRIB_SHORT_CODE === subFormula[d].ALERT_VALUE);
                if (targetName.length > 0) {
                  this.snackbar.openSnackBar(targetName[0].ATTRIB_DESCRIPTION, 'gtw-snackbar--yellow');
                }
              }
            }
          }
        }
      } catch (e) { /* bad json formula — ignore */ }
      return;
    }

    let formulaStr = formula;
    const matches = formulaStr.match(/\b[^\d\s]+\b/g);
    let lineFormulaArray: string[];
    if (matches == null) {
      lineFormulaArray = formulaStr.split(/[+\-/\\*\\]/);
    } else {
      const sub = formulaStr.substring(formulaStr.indexOf('('));
      lineFormulaArray = sub.split(/[+\-,/\\*\\]/);
    }

    for (let i = 0; i < lineFormulaArray.length; i++) {
      if (lineFormulaArray[i].indexOf('(') > -1 && lineFormulaArray[i].indexOf(')') > -1) {
        const tokenKey = lineFormulaArray[i].replace(/"/g, '').replace(/'/g, '').replace(/\(|\)/g, '').trim();
        const currentTabNewAttribVal = this.formListSchdA.filter((o: any) => o.ATTRIB_SHORT_CODE === tokenKey);
        if (currentTabNewAttribVal.length === 0) continue;
        const lineAmt = parseFloat(currentTabNewAttribVal[0].ATTRIB_VALUE);
        if (parseFloat(String(lineAmt)) >= 0) {
          formulaStr = formulaStr.replace('(' + tokenKey + ')', String(lineAmt));
        } else {
          formulaStr = formulaStr.replace('(' + tokenKey + ')', '(' + lineAmt + ')');
        }
      }
    }

    try {
      // eslint-disable-next-line no-eval
      const evalResult = safeEvalFormula(formulaStr, null, 'credits-part-iii-f8936-sch-a');
      if (evalResult != null && typeof evalResult !== 'undefined' && isFiniteEvalNumber(evalResult)) {
        const changedFormulaAttribVal = this.formListSchdA.filter((x: any) => x.LINE_NO === line_no && x.ATTRIB_COL === attrib_col);
        if (changedFormulaAttribVal.length > 0) {
          const oldVal = changedFormulaAttribVal[0].ATTRIB_VALUE;
          const newAttribValue = (formulaStr.substring(0, 4) === 'Math')
            ? evalResult
            : evalResult.toFixed();
          // Match legacy/F8936-main semantics: only flip IS_CHANGED_FLAG when
          // the recalculated value actually differs from the stored one — this
          // prevents init-pass on a vehicle with no prior input from forging
          // every label cell as "changed" and inflating the save payload.
          const isActuallyChanged = parseFloat(String(oldVal)) !== parseFloat(String(newAttribValue));
          changedFormulaAttribVal[0].ATTRIB_VALUE = newAttribValue;
          if (isActuallyChanged) {
            changedFormulaAttribVal[0].IS_CHANGED_FLAG = 'Y';
          }
        }
      }
    } catch (e) {
      // formula eval failed — skip this cell
    }
  }

  toDateInputFormat(value: any): string {
    if (!value) return '';
    const v: string = (value instanceof Date) ? this.dateToMDY(value) : String(value);
    const parts = v.split('/');
    if (parts.length !== 3) return v;
    return `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
  }

  private dateToMDY(d: Date): string {
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${m}/${day}/${d.getFullYear()}`;
  }

  saveSchdA(): void {
    if (this.isSaveClickedSchdA) return;
    const occurrenceForNew = (this.form8936SchdADtls != null && this.form8936SchdADtls.length > 0)
      ? Math.max(...this.form8936SchdADtls.map((o: any) => parseInt(String(o.OCCURRENCE), 10) || 0)) + 1
      : 1;
    const creditFormSaveDtlsSchdA: any[] = [];
    for (const list of this.allTabsListSchdA) {
      const changed = (list || []).filter((x: any) => x.IS_CHANGED_FLAG === 'Y');
      for (const row of changed) {
        let attribValue = row.ATTRIB_VALUE;
        if (row.EDIT_TYPE === 'custom_date_various' && row.ATTRIB_VALUE !== 'VARIOUS' && row.ATTRIB_VALUE instanceof Date) {
          attribValue = this.dateToMDY(row.ATTRIB_VALUE);
          row.ATTRIB_VALUE = attribValue;
        }
        creditFormSaveDtlsSchdA.push({
          form_name: row.formName || 'F8936SchA',
          issue_key: row.ISSUE_KEY,
          leid: row.LEID,
          cdr_number: row.CDR_NO,
          reporting_period: row.REPORTING_PERIOD,
          me_code: row.ME_CODE,
          part_no: row.partName || this.formDataObj?.partName || this.derivePartName(),
          schd_no: row.schdName || 'A',
          section_name: row.sectionName || this.formDataObj?.sectionName || '',
          combination_key: row.COMBINATION_KEY,
          form_key: row.FORM_KEY,
          form_line_key: row.FORM_LINE_KEY,
          line_attrib_key: row.LINE_ATTRIB_KEY,
          line_number: row.LINE_NO,
          attrib_type: row.ATTRIB_TYPE,
          attrib_prop_key: row.ATTRIB_PROP_KEY,
          acct_ref_key: row.ACCT_REF_KEY,
          attrib_value: attribValue,
          trans_details_key: row.TRANS_DETAILS_KEY,
          trans_type_key: row.TRANS_TYPE_KEY,
          is_changed_flag: row.IS_CHANGED_FLAG,
          occurrence: this.isEdit && parseInt(String(row.OCCURRENCE), 10) ? parseInt(String(row.OCCURRENCE), 10) : occurrenceForNew
        });
      }
    }

    if (creditFormSaveDtlsSchdA.length === 0) {
      this.snackbar.openSnackBar('No changes to save', 'gtw-snackbar--yellow');
      return;
    }

    this.isSaveClickedSchdA = true;
    this.cdr.detectChanges();
    const clobSettings = {
      sso_id: this.ssoId,
      screen_key: this.globalService?.screen_key || this.globalService?.globalParams?.screen_key,
      form_name: 'F8936SchA',
      combination_key: this.combinationKey,
      tax_year: this.currentYear,
      scenario: this.scenario,
      jcd_key: this.jcdKey,
      occurrence: creditFormSaveDtlsSchdA[0]?.occurrence
    };
    this.service.saveSchdA(this.baseUrl, creditFormSaveDtlsSchdA, clobSettings, this.globalService, this.filterParams).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (res: any) => {
        this.isSaveClickedSchdA = false;
        if (res?.errorMessage && res.errorMessage !== 'null') {
          this.snackbar.openSnackBar(res.errorMessage, 'gtw-snackbar--red');
        } else {
          this.snackbar.openSnackBar('Credits has been successfully saved/updated', 'gtw-snackbar--green');
          this.fetchAllDetails();
          this.resetEditState();
        }
        this.cdr.detectChanges();
      },
      () => {
        this.isSaveClickedSchdA = false;
        this.snackbar.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Reset reverts any edits in the editing template back to the template's
   * baseline by re-fetching it (using the same action_code as the original
   * Add or Edit), preserving the editing context.
   */
  resetSchdA(): void {
    if (this.isEdit) {
      // Find the matching occurrence row from the master list and re-edit it.
      const currentOcc = this.formListSchdA[0]?.OCCURRENCE;
      const item = this.form8936SchdADtls.find((o: any) => o.OCCURRENCE == currentOcc);
      if (item) {
        this.editRow(item);
        return;
      }
    }
    if (this.isNewClicked) {
      this.addNewRow();
      return;
    }
    this.discardEditState();
  }

  /**
   * Mirrors legacy `cancelFormSchdA` which dismisses the modal entirely
   * (regardless of whether the user is mid-edit on a Schedule A row).
   * Emits a `cancel` event so the parent modal can run its own close logic.
   */
  cancelFormSchdA(): void {
    this.cancel.emit();
  }

  private discardEditState(): void {
    this.isEdit = false;
    this.isNewClicked = false;
    this.formListSchdA = [];
    this.outerListSchdA = [];
    this.formHeaderSchdA = [];
    this.tableHeaderSchdA = [];
    this.allTabsListSchdA = [];
    this.cdr.detectChanges();
  }

  private resetEditState(): void {
    this.discardEditState();
  }

  trackByOccurrence(_: number, item: any): any {
    return item?.STAGE_KEY ?? item?.OCCURRENCE ?? _;
  }

  trackByLineAttribKey(_: number, row: any): any {
    return row?.LINE_ATTRIB_KEY ?? row?.FORM_LINE_KEY ?? _;
  }

  private safeParse(value: any): any {
    if (value == null) return value;
    if (typeof value !== 'string') return value;
    try {
      return safeJsonInput<any>(value, null, 'credits-part-iii-f8936-sch-a.parse3');
    } catch {
      return value;
    }
  }

  get hasEditingTemplate(): boolean {
    return (this.formListSchdA && this.formListSchdA.length > 0) || (this.outerListSchdA && this.outerListSchdA.length > 0);
  }
}
